import type { BooleanEntitlement, InvalidLicenseStatus, NumericEntitlement } from '@directus/license';
import { Monospace } from '@directus/license';
import * as z from 'zod/v4-mini';
import {
	LICENSE_INVALID_REASONS,
	LICENSE_SERVER_STATUSES,
	LICENSE_SOURCES,
} from '#layers/license/constants/license';

export type { LicenseStatus } from '#layers/base/public';
export { LicenseStatusSchema } from '#layers/base/public';

const LicenseInvalidReasonSchema = z.enum(LICENSE_INVALID_REASONS);

export type LicenseInvalidReason = InvalidLicenseStatus;

const LicenseSourceSchema = z.nullable(z.enum(LICENSE_SOURCES));

/**
 * `null` = no license (free tier);
 * `env-key` = online-revalidated key;
 * `env-token` = offline JWT.
 */
export type LicenseSource = z.infer<typeof LicenseSourceSchema>;

type LicenseEntitlementShape = typeof Monospace.Entitlements.out.shape;
type LicenseEntitlementShapeKey = keyof LicenseEntitlementShape;

export type LicenseEntitlementNumericKey = {
	[K in LicenseEntitlementShapeKey]: LicenseEntitlementShape[K] extends typeof NumericEntitlement.out ? K : never;
}[LicenseEntitlementShapeKey];

export type LicenseEntitlementBooleanKey = {
	[K in LicenseEntitlementShapeKey]: LicenseEntitlementShape[K] extends typeof BooleanEntitlement.out ? K : never;
}[LicenseEntitlementShapeKey];

type EntitlementValue<Shape> =
	& { [K in keyof Shape as undefined extends z.output<Shape[K]> ? never : K]: z.output<Shape[K]> }
	& { [K in keyof Shape as undefined extends z.output<Shape[K]> ? K : never]?: z.output<Shape[K]> };

export type LicenseEntitlements =
	& { [K in LicenseEntitlementNumericKey]?: EntitlementValue<typeof NumericEntitlement.out.shape> }
	& { [K in LicenseEntitlementBooleanKey]?: EntitlementValue<typeof BooleanEntitlement.out.shape> };

const PerWorkspaceUsageSchema = z.union([z.number(), z.record(z.string(), z.number())]);

const UsageSchema = z.object({
	workspaces: z.optional(z.number()),
	seats: z.optional(PerWorkspaceUsageSchema),
	custom_roles: z.optional(PerWorkspaceUsageSchema),
	custom_policies: z.optional(PerWorkspaceUsageSchema),
	service_accounts: z.optional(z.number()),
});

export type LicenseUsage = z.infer<typeof UsageSchema>;

// Drift guard: `UsageSchema`'s keys must stay exactly the numeric entitlement keys.
type AssertExtends<A extends B, B> = A;
type _EveryNumericEntitlementIsReportedInUsage = AssertExtends<LicenseEntitlementNumericKey, keyof LicenseUsage>;
type _EveryUsageKeyIsANumericEntitlement = AssertExtends<keyof LicenseUsage, LicenseEntitlementNumericKey>;

/**
 * Reachability of the licensing service as of the last refresh cycle. The engine
 * also sends a `reason` error chain on `unreachable`; the UI doesn't surface it.
 */
const LicenseServerStatusSchema = z.object({ status: z.enum(LICENSE_SERVER_STATUSES) });

const ManagementSchema = z.object({
	name: z.string(),
	source: LicenseSourceSchema,
	invalid_reason: z.nullable(LicenseInvalidReasonSchema),
	/** `null` for licenses that never call the service (free tier, offline token). */
	server_status: z.optional(z.nullable(LicenseServerStatusSchema)),
});

export type LicenseManagement = z.infer<typeof ManagementSchema>;

/** `status` lives on /system/info */
export const LicenseSchema = z.object({
	entitlements: Monospace.Entitlements.out.partial(),
	usage: UsageSchema,
	management: z.optional(ManagementSchema),
});

export type License = { entitlements: LicenseEntitlements } & Omit<z.infer<typeof LicenseSchema>, 'entitlements'>; // Omit entitlements to avoid zod's partial type inference
